import React, { useState } from "react";
import { Archive, MoreVertical, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  useDeleteCallScriptMutation,
  useGetCallScriptsQuery,
  useGetCampaignsQuery,
  useUpdateCallScriptMutation,
} from "@/store/rest_api";
import type { CallScript } from "@/model/interface/Call";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CallScriptFormDialog from "./CallScriptFormDialog";

export default function CallScriptsPage() {
  const { data: scripts = [], isLoading } = useGetCallScriptsQuery({ all: true });
  const { data: campaigns = [] } = useGetCampaignsQuery();
  const [updateScript] = useUpdateCallScriptMutation();
  const [archiveScript] = useDeleteCallScriptMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingScript, setEditingScript] = useState<CallScript | null>(null);
  const activeScripts = scripts.filter((script) => script.isActive);
  const archivedScripts = scripts.filter((script) => !script.isActive);

  const openCreate = () => {
    setEditingScript(null);
    setIsDialogOpen(true);
  };

  const openEdit = (script: CallScript) => {
    setEditingScript(script);
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (script: CallScript) => {
    try {
      await updateScript({ id: script.id, data: { isActive: !script.isActive } }).unwrap();
      toast.success(script.isActive ? "Script archived" : "Script restored");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not update the script"));
    }
  };

  const handleArchive = async (script: CallScript) => {
    try {
      await archiveScript(script.id).unwrap();
      toast.success("Script archived");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not archive the script"));
    }
  };

  const campaignsUsing = (script: CallScript) =>
    campaigns
      .filter((campaign) => campaign.settings?.defaultScriptId === script.id)
      .map((campaign) => campaign.name)
      .filter(Boolean)
      .join(", ");

  const renderRow = (script: CallScript) => {
    const usedBy = campaignsUsing(script);
    return (
      <div key={script.id} className="flex items-center gap-3 px-1 py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{script.name}</div>
          <p className="truncate text-xs text-muted-foreground">{script.body}</p>
          {usedBy && <p className="text-xs text-muted-foreground">Default for {usedBy}</p>}
        </div>
        <Switch checked={script.isActive} onCheckedChange={() => void handleToggleActive(script)} className="shrink-0" />
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => openEdit(script)}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" title="Options">
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-destructive" onClick={() => void handleArchive(script)}>
              <Archive className="mr-2 h-4 w-4" />
              Archive script
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Scripts agents read during power dialer calls</p>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Script
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Scripts</CardTitle>
          <CardDescription>
            {activeScripts.length} script{activeScripts.length !== 1 ? "s" : ""}. Set a campaign's default script in
            its dialer settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeScripts.length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">No scripts yet.</p>
          ) : (
            <div className="divide-y divide-border">{activeScripts.map(renderRow)}</div>
          )}
        </CardContent>
      </Card>

      {archivedScripts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Archived Scripts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">{archivedScripts.map(renderRow)}</div>
          </CardContent>
        </Card>
      )}

      <CallScriptFormDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} script={editingScript} />
    </div>
  );
}
